import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ChatMessage from '../ChatInterface/ChatMessage';
import { getWelcomeExampleQuestions } from '../ChatInterface/chatWelcome';
import WelcomeMessageContent from '../ChatInterface/WelcomeMessageContent';
import QueryInput from '../ChatInterface/QueryInput';
import ShareModal from './ShareModal';
import './ChatDrawerContent.css';

/**
 * ChatDrawerContent component - collapsible content area with messages and input
 * @param {Object} props - Component props
 * @param {Array} props.messages - Array of chat messages
 * @param {boolean} props.isLoading - Whether a request is in progress
 * @param {boolean} props.isInitializing - Whether opening message is being fetched
 * @param {function} props.onSendQuestion - Function to send questions
 * @param {function} props.onMessageClick - Function to handle message clicks
 * @param {string} props.selectedMessageId - ID of currently selected message
 * @param {function} props.onGraphClick - Callback when graph button is clicked
 * @param {string} [props.currentSessionId] - Backend session id for sharing
 * @returns {JSX.Element} ChatDrawerContent component
 */
const ChatDrawerContent = ({
  messages,
  isLoading,
  isInitializing = false,
  onSendQuestion,
  onMessageClick,
  selectedMessageId,
  onGraphClick,
  currentSessionId
}) => {
  const { t } = useTranslation();
  const messagesEndRef = useRef(null);
  const [shareOpen, setShareOpen] = useState(false);

  // Filter out hidden messages for display and count
  const visibleMessages = messages.filter((msg) => !msg.isHidden);
  const canShare =
    Boolean(currentSessionId) &&
    visibleMessages.some((msg) => msg.role === 'user') &&
    visibleMessages.some((msg) => msg.role === 'assistant');

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  return (
    <div className="chat-drawer-content">
      <div className="chat-drawer-messages">
        <div className="chat-drawer-messages-container">
          {!isInitializing && (
            <ChatMessage
              role="assistant"
              messageId="welcome"
              content={
                <WelcomeMessageContent
                  lead={t('chat.welcomeMessageLead')}
                  body={t('chat.welcomeMessageBody')}
                />
              }
              hideTimestamp
              suggestionsDisabled={isLoading}
              metadata={{
                followUpIntro: t('chat.welcomeQuestionsIntro'),
                followUpQuestions: getWelcomeExampleQuestions(t),
              }}
              onSuggestedQuestion={onSendQuestion}
            />
          )}
          {visibleMessages.map((message) => (
            <ChatMessage
              key={message.id}
              messageId={message.id}
              role={message.role}
              content={message.content}
              timestamp={message.timestamp}
              metadata={message.metadata}
              onClick={onMessageClick}
              isSelected={message.id === selectedMessageId}
              showGraphButton={!message.hideGraph}
              onGraphClick={onGraphClick}
              onSuggestedQuestion={onSendQuestion}
              suggestionsDisabled={isLoading}
            />
          ))}
          
          {isLoading && (
            <div className="chat-drawer-loading">
              <div className="loading-spinner"></div>
              <span>{t('chat.thinking')}</span>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>

      <div className="chat-drawer-input">
        <QueryInput
          onSendQuestion={onSendQuestion}
          isLoading={isLoading}
          canShare={canShare}
          onShare={() => setShareOpen(true)}
        />
      </div>
      <ShareModal
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        sessionId={currentSessionId}
      />
    </div>
  );
};

export default ChatDrawerContent;
